import { useContext, useMemo, useState } from 'react';
import Spinner from '@atlaskit/spinner';
import { BoardContextProvider } from './context/BoardContext';
import { ChatClient } from './components/ChatClient';
import PubNub from 'pubnub';
import { PubNubProvider } from 'pubnub-react';
import { useEffect } from 'react';
import { getAuthKey } from './api/boardChat';
import { TrelloPowerUpContext } from '@powerupsclub/trello';
import { PaymentsContext, PaymentsV3 } from '@powerupsclub/payments';
import { FeedbackPopup } from '@powerupsclub/feedback';

let hasOpenedPaymentsModal = false;
let openedPaymentsAt: Date | undefined;

function App() {
  const {
    isLoading: trelloIsLoading,
    me,
    trelloIframe,
    isAuthorized,
  } = useContext(TrelloPowerUpContext);
  const [authKey, setAuthKey] = useState<string | undefined>();

  const {
    isLoading: paymentsIsLoading,
    shouldShowPaymentModal,
    refetchSubscription,
  } = useContext(PaymentsContext);

  const loading = trelloIsLoading || paymentsIsLoading;

  const urlSearchParams = new URLSearchParams(window.location.search);
  const queryStrings = Object.fromEntries(urlSearchParams.entries());

  const pubnub = useMemo(() => {
    if (!me || !authKey) {
      return null;
    }

    return new PubNub({
      publishKey: 'pub-c-8b302e84-4578-4efd-bd0c-c91c1409b3ba',
      subscribeKey: 'sub-c-33094cfc-e444-11eb-b69b-aa8f176933f4',
      uuid: me.id,
      authKey,
    });
  }, [me, authKey]);

  useEffect(() => {
    (async () => {
      const jwt = await trelloIframe.jwt();
      const _authKey = await getAuthKey(jwt);
      setAuthKey(_authKey);
    })();
  }, [trelloIframe]);

  useEffect(() => {
    if (
      !loading &&
      isAuthorized &&
      shouldShowPaymentModal &&
      queryStrings.payment !== 'true' &&
      !hasOpenedPaymentsModal
    ) {
      trelloIframe.modal({
        url: './?payment=true',
        fullscreen: true,
      });

      hasOpenedPaymentsModal = true;
      openedPaymentsAt = new Date();
    }

    trelloIframe.render(async () => {
      if (openedPaymentsAt) {
        const lastSubscriptionReRender = await trelloIframe.get(
          'board',
          'private',
          'rerender:subscription'
        );

        if (lastSubscriptionReRender) {
          if (new Date(lastSubscriptionReRender) > openedPaymentsAt) {
            await refetchSubscription();

            openedPaymentsAt = undefined;
          }
        }
      }
    });
  }, [
    isAuthorized,
    loading,
    queryStrings.payment,
    refetchSubscription,
    shouldShowPaymentModal,
    trelloIframe,
  ]);

  if (loading) {
    return (
      <div className="loadingSpinnerContainer">
        <Spinner size="large" />
      </div>
    );
  }

  if (queryStrings.feedback === 'true') {
    return <FeedbackPopup />;
  }

  if (!pubnub) {
    return null;
  }

  if (queryStrings.payment === 'true') {
    return (
      <PaymentsV3
        onComplete={() => trelloIframe.closeModal()}
        onDismiss={() => trelloIframe.closeModal()}
      />
    );
  }

  return (
    <PubNubProvider client={pubnub}>
      <BoardContextProvider>
        <ChatClient />
      </BoardContextProvider>
    </PubNubProvider>
  );
}

export default App;
